import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
    IconButton,
    Tooltip,
    CircularProgress,
    Paper,
    Divider,
    Chip,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
} from "@mui/material";
import {
    AddOutlined,
    DeleteOutlined,
    EditOutlined,
    ExpandMoreOutlined,
    CodeOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import EndpointForm, { METHOD_COLORS } from "./EndpointForm";
import {
    useCreateEndpointsMutation,
    useDeleteEndpointMutation,
    useListEndpointsQuery,
} from "store/services/eBillProviderApi";

// Endpoint Card

const EndpointCard = ({ endpoint, onEdit, onDelete, isDeleting }) => {
    const [expanded, setExpanded] = useState(false);

    const renderValue = (value, isCredentialRef) => {
        if (isCredentialRef) {
            return (
                <Chip
                    label={value}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<CodeOutlined />}
                />
            );
        }
        return <span className="font-mono text-sm">{value}</span>;
    };

    return (
        <Accordion
            elevation={0}
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                <div className="flex w-full items-center justify-between pr-4">
                    <div className="flex items-center gap-3">
                        <Chip
                            label={endpoint.method}
                            size="small"
                            sx={{
                                bgcolor: METHOD_COLORS[endpoint.method],
                                color: "white",
                                fontWeight: "bold",
                            }}
                        />
                        <Typography className="font-medium">{endpoint.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {endpoint.path}
                        </Typography>
                    </div>

                    <div
                        className="flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onEdit(endpoint)}>
                                <EditOutlined fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                onClick={() => onDelete(endpoint.name)}
                                sx={{ color: "#ef4444" }}
                            >
                                {isDeleting ? (
                                    <CircularProgress size={18} />
                                ) : (
                                    <DeleteOutlined fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            </AccordionSummary>

            <AccordionDetails>
                <div className="space-y-4">
                    {endpoint.description && (
                        <Typography variant="body2" color="text.secondary">
                            {endpoint.description}
                        </Typography>
                    )}

                    {endpoint.queryParams?.length > 0 && (
                        <div>
                            <Typography variant="subtitle2" className="mb-2 font-semibold">
                                Query Parameters
                            </Typography>
                            <div className="space-y-1">
                                {endpoint.queryParams.map((q, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                        <span className="font-mono font-medium">{q.key}:</span>
                                        {renderValue(q.value, q.isCredentialRef)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {endpoint.bodyTemplate && (
                        <div>
                            <Typography variant="subtitle2" className="mb-2 font-semibold">
                                Body Template
                            </Typography>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    bgcolor: (theme) =>
                                        theme.palette.mode === "dark" ? "grey.900" : "grey.100",
                                    borderRadius: 1,
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    overflow: "auto",
                                }}
                            >
                                <pre>{JSON.stringify(endpoint.bodyTemplate, null, 2)}</pre>
                            </Paper>
                        </div>
                    )}
                </div>
            </AccordionDetails>
        </Accordion>
    );
};

// Main Tab

const EBillProviderEndpointsTab = () => {
    const { id } = useParams();
    const [showForm, setShowForm] = useState(false);
    const [editingEndpoint, setEditingEndpoint] = useState(null);

    const {
        data: endpointsData,
        isLoading: loadingEndpoints,
        refetch,
    } = useListEndpointsQuery(id, { skip: !id });

    const [createEndpoints, { isLoading: saving }] = useCreateEndpointsMutation();
    const [deleteEndpoint, { isLoading: deleting }] = useDeleteEndpointMutation();

    const endpoints = endpointsData?.data || [];

    const handleSave = async (endpointData) => {
        try {
            const payload = editingEndpoint
                ? { ...endpointData, _id: editingEndpoint._id }
                : endpointData;

            await createEndpoints({ id, endpoints: [payload] }).unwrap();
            toast.success(
                editingEndpoint
                    ? "Endpoint updated successfully"
                    : "Endpoint created successfully"
            );
            setShowForm(false);
            setEditingEndpoint(null);
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || "Failed to save endpoint");
        }
    };

    const handleEdit = (endpoint) => {
        setEditingEndpoint(endpoint);
        setShowForm(true);
    };

    const handleDelete = async (name) => {
        if (!window.confirm(`Are you sure you want to delete endpoint "${name}"?`)) return;
        try {
            await deleteEndpoint({ id, name }).unwrap();
            toast.success("Endpoint deleted successfully");
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || "Failed to delete endpoint");
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingEndpoint(null);
    };

    if (loadingEndpoints) {
        return (
            <div className="flex justify-center py-8">
                <CircularProgress size={28} />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold">API Endpoints</h3>
                    <p className="text-sm text-gray-500">
                        Configure API endpoints for this eBill provider
                    </p>
                </div>
                {!showForm && (
                    <Button
                        variant="contained"
                        startIcon={<AddOutlined />}
                        onClick={() => setShowForm(true)}
                    >
                        Add Endpoint
                    </Button>
                )}
            </div>

            {/* Form */}
            {showForm && (
                <EndpointForm
                    endpoint={editingEndpoint}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    isSaving={saving}
                />
            )}

            <Divider />

            {/* List */}
            <div>
                {/* <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-[#94a3b8]">
                    Configured Endpoints ({endpoints.length})
                </h3> */}

                {endpoints.length === 0 ? (
                    <p className="py-4 text-sm text-[#64748b]">
                        No endpoints configured yet. Click "Add Endpoint" to get started.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {endpoints.map((endpoint) => (
                            <EndpointCard
                                key={endpoint.name}
                                endpoint={endpoint}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                isDeleting={deleting}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EBillProviderEndpointsTab;